import {
  DebtReportGroupLabels,
  DebtServiceTypeLabels,
  PayableReportLabels,
  ReceivableReportLabels,
} from 'features/accounting/debts/constants';
import {
  DATA_IMPORT_TYPE,
  DATA_PROCESSING_STEP_TYPE,
  DEBT_REPORT_GROUP,
  DEBT_SERVICE_TYPE,
  PAYABLE_REPORT_KEY,
  RECEIVABLE_REPORT_KEY,
} from 'features/accounting/debts/types';
import { E_OFFICE_MANAGEMENT_TYPE } from 'features/content-template/types';
import { INTERNAL_DOCUMENT_TYPE } from 'features/internal-document/common/types';
import { PROPOSAL_PERMISSION } from 'features/proposal/types';
import { WORK_TYPE } from 'features/work/types';
import { createElement } from 'react';
import { Navigate } from 'react-router-dom';
import { lazyImport } from 'routes/helpers';
import { getPath } from 'routes/router-paths';
import {
  ACCOUNTING_MANAGEMENT_TAB,
  type DIGITAL_SIGN_TAB,
  SYSTEM_CATEGORY_TAB,
  type TRoute,
  type UTILITY_MANAGEMENT_TAB,
} from 'routes/types';
import { AccountingManagementTabLabels } from 'modules/Accounting/Management/AccountingManagementPage/constants';

const InternalDocTabLabels: Record<INTERNAL_DOCUMENT_TYPE, string> = {
  [INTERNAL_DOCUMENT_TYPE.INCOMING]: 'Văn bản đến',
  [INTERNAL_DOCUMENT_TYPE.OUTGOING]: 'Văn bản đi',
  [INTERNAL_DOCUMENT_TYPE.E_SIGN]: 'Phát hành văn bản điện tử',
};

const WorkTypeTabLabels: Record<WORK_TYPE, string> = {
  [WORK_TYPE.ALL]: 'Tất cả',
  [WORK_TYPE.DOCUMENT]: 'Từ văn bản',
  [WORK_TYPE.INTERNAL]: 'Nội bộ trong Ban',
  [WORK_TYPE.CONSULT]: 'Xin ý kiến các Ban',
  [WORK_TYPE.ELECTRONIC]: 'Trình ký văn bản',
  [WORK_TYPE.DEPLOY]: 'Từ nhiệm vụ',
  [WORK_TYPE.POLICY]: 'Xin chủ trương',
  [WORK_TYPE.OTHER]: 'Khác',
};

const SystemCategoryTabLabels: Record<SYSTEM_CATEGORY_TAB, string> = {
  [SYSTEM_CATEGORY_TAB.DEPARTMENT]: 'Phòng ban',
  [SYSTEM_CATEGORY_TAB.POSITION]: 'Chức danh',
};

const ProposalPermissionTabLabels: Record<PROPOSAL_PERMISSION, string> = {
  [PROPOSAL_PERMISSION.CREATE]: 'Đã gửi',
  [PROPOSAL_PERMISSION.PIC]: 'Xử lý',
  [PROPOSAL_PERMISSION.VIEW]: 'Theo dõi',
};

const EOfficeManagementPageLabels: Record<E_OFFICE_MANAGEMENT_TYPE, string> = {
  [E_OFFICE_MANAGEMENT_TYPE.APPROVAL_TEMPLATE]: 'Cấp duyệt',
  [E_OFFICE_MANAGEMENT_TYPE.ADMIN_TYPE]: 'Loại hành chính',
  [E_OFFICE_MANAGEMENT_TYPE.CONTENT_TEMPLATE]: 'Văn bản mẫu',
};

export const publicRoutes: TRoute[] = [
  {
    path: getPath('loginPage'),
    element: createElement(
      lazyImport(() => import('modules/Authentication/LoginPage')),
      { pageTitle: 'Đăng nhập' }
    ),
  },

  {
    path: getPath('forbiddenPage'),
    element: createElement(
      lazyImport(() =>
        import('shared/components/ForbiddenAccessNotice').then(m => ({
          default: m.ForbiddenAccessNotice,
        }))
      )
    ),
  },
];

export const privateRoutes: TRoute[] = [
  // ---------------------------------------------------------------------------
  // Module: Trang chủ (HOME)
  // ---------------------------------------------------------------------------

  /** Trang dashboard tổng quan — màn hình chính sau khi đăng nhập */
  {
    path: getPath('dashboardPage'),
    element: createElement(
      lazyImport(() => import('modules/Miscellaneous/DashboardPage')),
      { pageTitle: 'Trang chủ' }
    ),
  },

  /** Trang thông tin nội bộ — nhúng báo cáo Power BI */
  {
    path: getPath('reportPage'),
    element: createElement(
      lazyImport(() => import('modules/Report/ReportPage'))
    ),
    breadcrumbs: [{ title: 'Trang chủ' }, { title: 'Thông tin nội bộ' }],
  },

  // ---------------------------------------------------------------------------
  // Module: Văn bản (DOCUMENT)
  // ---------------------------------------------------------------------------

  /** Danh sách văn bản nội bộ — phân loại theo type: văn bản đến / đi / phát hành điện tử */
  {
    path: getPath(
      'internalDocumentsListPage',
      ':pageType' as INTERNAL_DOCUMENT_TYPE
    ),
    element: createElement(
      lazyImport(() => import('modules/Documents/InternalDocumentsListPage'))
    ),
    breadcrumbs: params => [
      { title: 'Văn bản' },
      {
        title: 'Văn bản nội bộ',
        path: getPath(
          'internalDocumentsListPage',
          INTERNAL_DOCUMENT_TYPE.INCOMING
        ),
      },
      {
        title:
          InternalDocTabLabels[params.pageType as INTERNAL_DOCUMENT_TYPE] ?? '',
      },
    ],
  },

  /** Chi tiết một văn bản nội bộ — xem nội dung, lịch sử xử lý */
  {
    path: getPath(
      'internalDocumentDetailPage',
      ':pageType' as INTERNAL_DOCUMENT_TYPE,
      ':documentCode'
    ),
    element: createElement(
      lazyImport(() => import('modules/Documents/InternalDocumentDetailPage'))
    ),
    breadcrumbs: params => [
      { title: 'Văn bản' },
      {
        title: 'Văn bản nội bộ',
        path: getPath(
          'internalDocumentsListPage',
          INTERNAL_DOCUMENT_TYPE.INCOMING
        ),
      },
      {
        title:
          InternalDocTabLabels[params.pageType as INTERNAL_DOCUMENT_TYPE] ?? '',
        path: getPath(
          'internalDocumentsListPage',
          (params.pageType ??
            INTERNAL_DOCUMENT_TYPE.INCOMING) as INTERNAL_DOCUMENT_TYPE
        ),
      },
      { title: 'Chi tiết văn bản' },
    ],
  },

  // ---------------------------------------------------------------------------
  // Module: e-Office (Công việc & Đơn hành chính)
  // ---------------------------------------------------------------------------

  /** Danh sách công việc — lọc theo loại: tất cả / giao việc / nhận việc */
  {
    path: getPath('workListPage', ':workType' as WORK_TYPE),
    element: createElement(
      lazyImport(() => import('modules/EOffice/WorkListPage'))
    ),
    breadcrumbs: params => [
      { title: 'Công việc', path: getPath('workListPage', WORK_TYPE.ALL) },
      { title: WorkTypeTabLabels[params.workType as WORK_TYPE] ?? '' },
    ],
  },

  /** Chi tiết một công việc — xem tiến độ, bình luận, file đính kèm */
  {
    path: getPath('workDetailPage', ':workCode'),
    element: createElement(
      lazyImport(() => import('modules/EOffice/WorkDetailPage'))
    ),
    breadcrumbs: [
      { title: 'Công việc', path: getPath('workListPage', WORK_TYPE.ALL) },
      { title: 'Chi tiết công việc' },
    ],
  },

  /** Danh sách đơn hành chính — nghỉ phép, công tác, tăng ca… */
  {
    path: getPath('proposalListPage'),
    element: createElement(
      lazyImport(() => import('modules/EOffice/ProposalListPage'))
    ),
    breadcrumbs: (_params, searchParams) => {
      const permission = searchParams.get('proposal_permission') ?? '';
      const tabLabel = permission
        ? ProposalPermissionTabLabels[permission as PROPOSAL_PERMISSION]
        : null;
      return [
        { title: 'e-Office' },
        {
          title: 'Đơn hành chính',
          path: getPath('proposalListPage'),
        },
        ...(tabLabel ? [{ title: tabLabel }] : []),
      ];
    },
  },

  /** Chi tiết một đơn hành chính — xem trạng thái duyệt, lịch sử */
  {
    path: getPath('proposalDetailPage', ':proposalCode'),
    element: createElement(
      lazyImport(
        () => import('modules/EOffice/ProposalDetailPage/ProposalDetailPage')
      )
    ),
    breadcrumbs: (_params, searchParams) => {
      const permission = searchParams.get('proposal_permission') ?? '';
      const tabLabel = permission
        ? ProposalPermissionTabLabels[permission as PROPOSAL_PERMISSION]
        : null;
      const tabPath = permission
        ? `${getPath('proposalListPage')}?proposal_permission=${permission}`
        : getPath('proposalListPage');
      return [
        { title: 'e-Office' },
        {
          title: 'Đơn hành chính',
          path: getPath('proposalListPage'),
        },
        ...(tabLabel ? [{ title: tabLabel, path: tabPath }] : []),
        { title: 'Chi tiết đơn hành chính' },
      ];
    },
  },

  /** Trang quản trị e-Office — cấu hình mẫu phê duyệt, template quy trình */
  {
    path: getPath(
      'eOfficeManagementPage',
      ':pageType' as E_OFFICE_MANAGEMENT_TYPE
    ),
    element: createElement(
      lazyImport(() => import('modules/EOffice/EOfficeManagementPage'))
    ),
    breadcrumbs: params => [
      { title: 'e-Office' },
      { title: 'Quản trị e-Office' },
      {
        title:
          EOfficeManagementPageLabels[
            params.pageType as E_OFFICE_MANAGEMENT_TYPE
          ],
      },
    ],
  },

  // ---------------------------------------------------------------------------
  // Module: Nhiệm vụ (TASK) — tạm ẩn, chưa triển khai
  // ---------------------------------------------------------------------------

  /** Trang danh sách Nhiệm vụ */
  {
    path: getPath('taskListPage'),
    element: createElement(
      lazyImport(() => import('modules/Task/TaskListPage'))
    ),
    breadcrumbs: [{ title: 'Nhiệm vụ' }, { title: 'Danh sách nhiệm vụ' }],
  },

  /** Trang quản trị quyền module Nhiệm vụ */
  {
    path: getPath('taskManagementPermission'),
    element: createElement(
      lazyImport(() => import('modules/Task/TaskManagement'))
    ),
    breadcrumbs: [{ title: 'Nhiệm vụ' }, { title: 'Quản trị nhiệm vụ' }],
  },

  // ---------------------------------------------------------------------------
  // Module: Bảng lương (SALARY) — tạm ẩn, chưa triển khai
  // ---------------------------------------------------------------------------

  /** Trang quản trị quyền module Bảng lương */
  {
    path: getPath('salaryManagementPermission'),
    element: createElement(
      lazyImport(() => import('modules/Salary/SalaryManagement'))
    ),
  },

  // ---------------------------------------------------------------------------
  // Module: Kiểm soát rủi ro (RISK) — tạm ẩn, chưa triển khai
  // ---------------------------------------------------------------------------

  /** Trang quản trị quyền module Kiểm soát rủi ro */
  {
    path: getPath('riskManagementPermission'),
    element: createElement(
      lazyImport(() => import('modules/Risk/RiskManagement'))
    ),
  },

  // ---------------------------------------------------------------------------
  // Module: Tiện ích (UTILITY)
  // ---------------------------------------------------------------------------

  /** Lịch cơ quan — xem lịch làm việc, sự kiện của công ty / chi nhánh; companyCode dùng query param */
  {
    path: getPath('workSchedulePage'),
    element: createElement(
      lazyImport(() => import('modules/WorkSchedule/WorkSchedulePage'))
    ),
    breadcrumbs: [{ title: 'Tiện ích' }, { title: 'Lịch cơ quan' }],
  },

  /** Tạo mới lịch cơ quan */
  {
    path: getPath('workScheduleCreate'),
    element: createElement(
      lazyImport(() => import('modules/WorkSchedule/WorkScheduleCreatePage'))
    ),
    breadcrumbs: [
      { title: 'Tiện ích' },
      { title: 'Lịch cơ quan', path: getPath('workSchedulePage') },
      { title: 'Tạo lịch cơ quan' },
    ],
  },

  /** Chỉnh sửa lịch cơ quan theo mã lịch */
  {
    path: getPath('workScheduleUpdate', ':scheduleCode'),
    element: createElement(
      lazyImport(() => import('modules/WorkSchedule/WorkScheduleUpdatePage'))
    ),
    breadcrumbs: [
      { title: 'Tiện ích' },
      { title: 'Lịch cơ quan', path: getPath('workSchedulePage') },
      { title: 'Chỉnh sửa lịch cơ quan' },
    ],
  },

  /** Danh bạ công ty — xem thông tin liên lạc toàn bộ nhân sự */
  {
    path: getPath('contactPage'),
    element: createElement(
      lazyImport(() => import('modules/Ultilities/Contact/ContactPage'))
    ),
    breadcrumbs: [{ title: 'Tiện ích' }, { title: 'Liên hệ' }],
  },

  /** Ký số điện tử — tab optional trên route; thiếu tab → page tự chuyển tab mặc định */
  {
    path: getPath('digitalSignaturePage', ':tabType?' as DIGITAL_SIGN_TAB),
    element: createElement(
      lazyImport(() => import('modules/Ultilities/DigitalSignPage'))
    ),
    breadcrumbs: [{ title: 'Tiện ích' }, { title: 'Ký số điện tử' }],
  },

  /** Trang quản trị tiện ích — cấu hình lịch cơ quan, danh bạ… */
  {
    path: getPath(
      'utilityManagementPage',
      ':pageType' as UTILITY_MANAGEMENT_TAB
    ),
    element: createElement(
      lazyImport(() => import('modules/Ultilities/UtilityManagementPage'))
    ),
    breadcrumbs: [{ title: 'Tiện ích' }, { title: 'Quản trị tiện ích' }],
  },
  {
    path: getPath('newsListPage'),
    element: createElement(
      lazyImport(() => import('modules/Ultilities/NewsPage')),
      { pageTitle: 'Thông báo chung' }
    ),
    breadcrumbs: [{ title: 'Tiện ích' }, { title: 'Thông báo chung' }],
  },

  /** Tài liệu cá nhân — quản lý thư mục / tài liệu của tôi và tài liệu được chia sẻ */
  {
    path: getPath('myDocumentPage'),
    element: createElement(
      lazyImport(() => import('modules/Ultilities/MyDocumentPage')),
      { pageTitle: 'Tài liệu' }
    ),
    breadcrumbs: [{ title: 'Tiện ích' }, { title: 'Tài liệu' }],
  },

  // ---------------------------------------------------------------------------
  // Module: Thông báo (Notification) — dùng chung, không thuộc nhóm sidebar
  // ---------------------------------------------------------------------------

  /** Danh sách thông báo hệ thống của người dùng */
  {
    path: getPath('notificationListPage'),
    element: createElement(
      lazyImport(() => import('modules/Notification/NotificationListPage')),
      { pageTitle: 'Thông báo' }
    ),
    breadcrumbs: [
      { title: 'Trang chủ', path: getPath('dashboardPage') },
      { title: 'Thông báo' },
    ],
  },

  // ---------------------------------------------------------------------------
  // Module: Quản trị hệ thống (ADMIN_SYSTEM)
  // ---------------------------------------------------------------------------

  /** Trang phân quyền hệ thống — gán quyền cho người dùng / nhóm */
  {
    path: getPath('managementPermission'),
    element: createElement(
      lazyImport(() => import('modules/Management/AclGroupPage'))
    ),
    breadcrumbs: [{ title: 'Quản trị' }, { title: 'Phân quyền' }],
  },

  /** Trang chi tiết nhóm quyền */
  {
    path: getPath('aclGroupDetailPage', ':code'),
    element: createElement(
      lazyImport(() => import('modules/Management/AclGroupDetailPage'))
    ),
    breadcrumbs: [
      { title: 'Quản trị' },
      { title: 'Phân quyền', path: getPath('managementPermission') },
      { title: 'Thiết lập phân quyền' },
    ],
  },

  /** Trang quản lý tài khoản — thêm / sửa / khóa tài khoản người dùng */
  {
    path: getPath('accountManagementPage'),
    element: createElement(
      lazyImport(() => import('modules/Management/AccountPage'))
    ),
    breadcrumbs: [{ title: 'Quản trị' }, { title: 'Tài khoản' }],
  },

  /** Trang tài khoản ký số — khai báo tài khoản chứng thư số để ký số điện tử */
  {
    path: getPath('signAccountManagementPage'),
    element: createElement(
      lazyImport(() => import('modules/Management/SignAccountPage'))
    ),
    breadcrumbs: [{ title: 'Quản trị' }, { title: 'Tài khoản ký số' }],
  },

  /** Trang danh mục hệ thống — phòng ban, chức vụ, loại văn bản… */
  {
    path: getPath('systemCategoryPage', ':pageType' as SYSTEM_CATEGORY_TAB),
    element: createElement(
      lazyImport(
        () => import('modules/Management/SystemCategoryPage/SystemCategoryPage')
      )
    ),
    breadcrumbs: params => [
      { title: 'Quản trị' },
      {
        title: 'Danh mục hệ thống',
        path: getPath('systemCategoryPage', SYSTEM_CATEGORY_TAB.DEPARTMENT),
      },
      {
        title:
          SystemCategoryTabLabels[params.pageType as SYSTEM_CATEGORY_TAB] ?? '',
      },
    ],
  },

  /** Trang quản lý module — bật / tắt các module trong hệ thống */
  {
    path: getPath('moduleManagementPage'),
    element: createElement(
      lazyImport(
        () =>
          import('modules/Management/ModuleManagementPage/ModuleManagementPage')
      )
    ),
    breadcrumbs: [{ title: 'Quản trị' }, { title: 'Module' }],
  },

  // ---------------------------------------------------------------------------
  // Module: Kế toán (ACCOUNTING)
  // ---------------------------------------------------------------------------

  // Công nợ - Xử lý dữ liệu (fixed step routing: /accounting/debts/data-processing/:processingType)
  {
    path: getPath(
      'accountingDebtDataProcessingStepPage',
      ':processingType' as unknown as DATA_PROCESSING_STEP_TYPE
    ),
    element: createElement(
      lazyImport(
        () => import('modules/Accounting/Debts/DataReconciliationPage')
      ),
      { pageTitle: 'Xử lý dữ liệu' }
    ),
    breadcrumbs: () => [
      { title: 'Kế toán' },
      { title: 'Công nợ' },
      {
        title: 'Xử lý dữ liệu',
      },
    ],
  },
  // Default /accounting/debts/data-processing redirects to /accounting/debts/data-processing/import?type=providers
  {
    path: getPath('accountingDebtDataProcessingPage'),
    element: createElement(Navigate, {
      to: `${getPath(
        'accountingDebtDataProcessingStepPage',
        DATA_PROCESSING_STEP_TYPE.IMPORT
      )}?type=${DATA_IMPORT_TYPE.PROVIDERS}`,
      replace: true,
    }),
  },

  // Legacy redirects from old URLs
  {
    path: '/accounting/debts/data-processing/:serviceType/reconciliation',
    element: createElement(Navigate, {
      to: getPath(
        'accountingDebtDataProcessingStepPage',
        DATA_PROCESSING_STEP_TYPE.RECONCILIATION
      ),
      replace: true,
    }),
  },
  {
    path: '/accounting/debts/data-processing/:serviceType/comparison',
    element: createElement(Navigate, {
      to: getPath(
        'accountingDebtDataProcessingStepPage',
        DATA_PROCESSING_STEP_TYPE.RECONCILIATION
      ),
      replace: true,
    }),
  },
  {
    path: '/accounting/debts/data-processing/comparison',
    element: createElement(Navigate, {
      to: getPath(
        'accountingDebtDataProcessingStepPage',
        DATA_PROCESSING_STEP_TYPE.RECONCILIATION
      ),
      replace: true,
    }),
  },
  {
    path: '/accounting/debts/data-processing/:dataType/:serviceType/reconciliation',
    element: createElement(Navigate, {
      to: getPath(
        'accountingDebtDataProcessingStepPage',
        DATA_PROCESSING_STEP_TYPE.RECONCILIATION
      ),
      replace: true,
    }),
  },
  {
    path: '/accounting/debts/data-processing/:dataType/:serviceType/comparison',
    element: createElement(Navigate, {
      to: getPath(
        'accountingDebtDataProcessingStepPage',
        DATA_PROCESSING_STEP_TYPE.RECONCILIATION
      ),
      replace: true,
    }),
  },
  {
    path: '/accounting/debts/data-processing/:dataType/:serviceType',
    element: createElement(Navigate, {
      to: `${getPath(
        'accountingDebtDataProcessingStepPage',
        DATA_PROCESSING_STEP_TYPE.IMPORT
      )}?type=${DATA_IMPORT_TYPE.PROVIDERS}`,
      replace: true,
    }),
  },
  {
    path: '/accounting/debts/data-processing/:serviceType',
    element: createElement(Navigate, {
      to: `${getPath(
        'accountingDebtDataProcessingStepPage',
        DATA_PROCESSING_STEP_TYPE.IMPORT
      )}?type=${DATA_IMPORT_TYPE.PROVIDERS}`,
      replace: true,
    }),
  },

  // Công nợ - Dữ liệu treo
  {
    path: getPath(
      'accountingDebtPendingDataPage',
      ':serviceType' as DEBT_SERVICE_TYPE
    ),
    element: createElement(
      lazyImport(() => import('modules/Accounting/Debts/DataProcessingPage')),
      { mode: 'pending', pageTitle: 'Dữ liệu treo' }
    ),
    breadcrumbs: params => [
      { title: 'Kế toán' },
      { title: 'Công nợ' },
      {
        title: 'Dữ liệu treo',
        path: getPath('accountingDebtPendingDataPage'),
      },
      {
        title:
          DebtServiceTypeLabels[params.serviceType as DEBT_SERVICE_TYPE] ??
          'Vé máy bay',
      },
    ],
  },
  {
    path: getPath('accountingDebtPendingDataPage'),
    element: createElement(Navigate, {
      to: getPath('accountingDebtPendingDataPage', DEBT_SERVICE_TYPE.AIRLINES),
      replace: true,
    }),
  },
  // Legacy redirects from old URLs (/accounting/debts/pending/:dataType/:serviceType)
  {
    path: '/accounting/debts/pending/:dataType/:serviceType',
    element: createElement(
      lazyImport(() => import('modules/Accounting/Debts/DataProcessingPage')),
      { mode: 'pending', pageTitle: 'Dữ liệu treo' }
    ),
  },
  {
    path: '/accounting/debts/pending/:dataType',
    element: createElement(Navigate, {
      to: getPath('accountingDebtPendingDataPage', DEBT_SERVICE_TYPE.AIRLINES),
      replace: true,
    }),
  },

  // Công nợ - Báo cáo: Đối soát dữ liệu báo cáo giao dịch (Reconciliation)
  {
    path: getPath('accountingReceivableTransactionReportReconciliationPage'),
    element: createElement(
      lazyImport(
        () =>
          import(
            'modules/Accounting/Receivables/TransactionReportReconciliationPage'
          )
      ),
      { pageTitle: 'Đối soát dữ liệu báo cáo giao dịch' }
    ),
    breadcrumbs: [
      { title: 'Kế toán' },
      { title: 'Công nợ' },
      {
        title: 'Báo cáo',
        path: getPath('accountingDebtReportsPage'),
      },
      {
        title: 'Phải thu',
        path: getPath(
          'accountingDebtReportsPage',
          DEBT_REPORT_GROUP.RECEIVABLES,
          RECEIVABLE_REPORT_KEY.TRANSACTION_REPORT
        ),
      },
      {
        title: 'Báo cáo giao dịch',
        path: getPath(
          'accountingDebtReportsPage',
          DEBT_REPORT_GROUP.RECEIVABLES,
          RECEIVABLE_REPORT_KEY.TRANSACTION_REPORT
        ),
      },
      {
        title: 'Đối soát dữ liệu',
      },
    ],
  },

  // Công nợ - Báo cáo (có phân loại dịch vụ: airlines / hotel)
  {
    path: getPath(
      'accountingDebtReportsPage',
      ':group' as DEBT_REPORT_GROUP,
      ':reportKey',
      ':serviceType'
    ),
    element: createElement(
      lazyImport(() => import('modules/Accounting/Debts/ReportsPage')),
      { pageTitle: 'Báo cáo' }
    ),
    breadcrumbs: params => [
      { title: 'Kế toán' },
      { title: 'Công nợ' },
      {
        title: 'Báo cáo',
        path: getPath('accountingDebtReportsPage'),
      },
      {
        title:
          DebtReportGroupLabels[params.group as DEBT_REPORT_GROUP] ??
          'Phải thu',
      },
      {
        title:
          (params.group === DEBT_REPORT_GROUP.PAYABLES
            ? PayableReportLabels[params.reportKey as PAYABLE_REPORT_KEY]
            : ReceivableReportLabels[
                params.reportKey as RECEIVABLE_REPORT_KEY
              ]) ?? '',
      },
      ...(params.serviceType
        ? [
            {
              title:
                DebtServiceTypeLabels[
                  params.serviceType as DEBT_SERVICE_TYPE
                ] ??
                (params.serviceType === 'hotel' ? 'Khách sạn' : 'Vé máy bay'),
            },
          ]
        : []),
    ],
  },

  // Công nợ - Báo cáo
  {
    path: getPath(
      'accountingDebtReportsPage',
      ':group' as DEBT_REPORT_GROUP,
      ':reportKey'
    ),
    element: createElement(
      lazyImport(() => import('modules/Accounting/Debts/ReportsPage')),
      { pageTitle: 'Báo cáo' }
    ),
    breadcrumbs: params => [
      { title: 'Kế toán' },
      { title: 'Công nợ' },
      {
        title: 'Báo cáo',
        path: getPath('accountingDebtReportsPage'),
      },
      {
        title:
          DebtReportGroupLabels[params.group as DEBT_REPORT_GROUP] ??
          'Phải thu',
      },
      {
        title:
          (params.group === DEBT_REPORT_GROUP.PAYABLES
            ? PayableReportLabels[params.reportKey as PAYABLE_REPORT_KEY]
            : ReceivableReportLabels[
                params.reportKey as RECEIVABLE_REPORT_KEY
              ]) ?? '',
      },
    ],
  },
  {
    path: getPath('accountingDebtReportsPage'),
    element: createElement(Navigate, {
      to: getPath(
        'accountingDebtReportsPage',
        DEBT_REPORT_GROUP.RECEIVABLES,
        RECEIVABLE_REPORT_KEY.SALES_REPORT
      ),
      replace: true,
    }),
  },

  // Số dư đầu kỳ (chuyển hướng sang Quản trị module Kế toán dạng tab)
  {
    path: getPath('accountingOpeningBalanceReceivablePage'),
    element: createElement(Navigate, {
      to: getPath(
        'accountingManagementPage',
        ACCOUNTING_MANAGEMENT_TAB.RECEIVABLES
      ),
      replace: true,
    }),
  },
  {
    path: getPath('accountingOpeningBalanceSupplierPage'),
    element: createElement(Navigate, {
      to: getPath(
        'accountingManagementPage',
        ACCOUNTING_MANAGEMENT_TAB.SUPPLIERS
      ),
      replace: true,
    }),
  },

  // Thuế
  {
    path: getPath('accountingTaxInvoiceRequestPage'),
    element: createElement(
      lazyImport(() => import('modules/Accounting/Tax/InvoiceRequestPage')),
      { pageTitle: 'Yêu cầu hoá đơn' }
    ),
    breadcrumbs: [
      { title: 'Kế toán' },
      { title: 'Thuế' },
      { title: 'Yêu cầu hoá đơn' },
    ],
  },
  {
    path: getPath('accountingTaxIssuedInvoicePage'),
    element: createElement(
      lazyImport(() => import('modules/Accounting/Tax/IssuedInvoicePage')),
      { pageTitle: 'Hoá đơn xuất' }
    ),
    breadcrumbs: [
      { title: 'Kế toán' },
      { title: 'Thuế' },
      { title: 'Hoá đơn xuất' },
    ],
  },

  // Tổng hợp công nợ
  {
    path: getPath('accountingCustomerDebtSummaryPage'),
    element: createElement(
      lazyImport(
        () => import('modules/Accounting/DebtSummary/CustomerDebtSummaryPage')
      ),
      { pageTitle: 'Tổng hợp công nợ khách hàng' }
    ),
    breadcrumbs: [
      { title: 'Kế toán' },
      { title: 'Tổng hợp công nợ' },
      { title: 'Tổng hợp công nợ khách hàng' },
    ],
  },
  {
    path: getPath('accountingSupplierDebtSummaryPage'),
    element: createElement(
      lazyImport(
        () => import('modules/Accounting/DebtSummary/SupplierDebtSummaryPage')
      ),
      { pageTitle: 'Tổng hợp công nợ nhà cung cấp' }
    ),
    breadcrumbs: [
      { title: 'Kế toán' },
      { title: 'Tổng hợp công nợ' },
      { title: 'Tổng hợp công nợ nhà cung cấp' },
    ],
  },

  // Quản trị module Kế toán
  {
    path: getPath(
      'accountingManagementPage',
      ':tab?' as ACCOUNTING_MANAGEMENT_TAB
    ),
    element: createElement(
      lazyImport(
        () => import('modules/Accounting/Management/AccountingManagementPage')
      ),
      { pageTitle: 'Quản trị module Kế toán' }
    ),
    breadcrumbs: (params: Record<string, string | undefined>) => {
      const tabParam = params.tab as ACCOUNTING_MANAGEMENT_TAB | undefined;
      const subTitle = tabParam && AccountingManagementTabLabels[tabParam];
      return [
        { title: 'Kế toán' },
        { title: 'Quản trị module Kế toán' },
        ...(subTitle ? [{ title: subTitle }] : []),
      ];
    },
  },

  // ---------------------------------------------------------------------------
  // Legacy Accounting redirects
  // ---------------------------------------------------------------------------
  {
    path: '/accounting/data-processing/sales/*',
    element: createElement(Navigate, {
      to: getPath(
        'accountingDebtDataProcessingPage',
        DEBT_SERVICE_TYPE.AIRLINES
      ),
      replace: true,
    }),
  },
  {
    path: '/accounting/data-processing/refunds/*',
    element: createElement(Navigate, {
      to: getPath(
        'accountingDebtDataProcessingPage',
        DEBT_SERVICE_TYPE.AIRLINES
      ),
      replace: true,
    }),
  },
  {
    path: '/accounting/data-processing/bank',
    element: createElement(Navigate, {
      to: `${getPath(
        'accountingDebtDataProcessingStepPage',
        DATA_PROCESSING_STEP_TYPE.IMPORT
      )}?type=${DATA_IMPORT_TYPE.BANK}`,
      replace: true,
    }),
  },
  {
    path: '/accounting/receivables/sales-report',
    element: createElement(Navigate, {
      to: getPath(
        'accountingDebtReportsPage',
        DEBT_REPORT_GROUP.RECEIVABLES,
        RECEIVABLE_REPORT_KEY.SALES_REPORT
      ),
      replace: true,
    }),
  },
  {
    path: '/accounting/receivables/transaction-report',
    element: createElement(Navigate, {
      to: getPath(
        'accountingDebtReportsPage',
        DEBT_REPORT_GROUP.RECEIVABLES,
        RECEIVABLE_REPORT_KEY.TRANSACTION_REPORT
      ),
      replace: true,
    }),
  },
  {
    path: '/accounting/receivables/debt-report',
    element: createElement(Navigate, {
      to: getPath(
        'accountingDebtReportsPage',
        DEBT_REPORT_GROUP.RECEIVABLES,
        RECEIVABLE_REPORT_KEY.DEBT_REPORT
      ),
      replace: true,
    }),
  },
  {
    path: '/accounting/receivables/opening-balance',
    element: createElement(Navigate, {
      to: getPath(
        'accountingManagementPage',
        ACCOUNTING_MANAGEMENT_TAB.RECEIVABLES
      ),
      replace: true,
    }),
  },
  {
    path: '/accounting/payables/provider-topup',
    element: createElement(Navigate, {
      to: getPath(
        'accountingDebtReportsPage',
        DEBT_REPORT_GROUP.PAYABLES,
        PAYABLE_REPORT_KEY.PROVIDER_TOPUP
      ),
      replace: true,
    }),
  },
  {
    path: '/accounting/payables/debt-report',
    element: createElement(Navigate, {
      to: getPath(
        'accountingDebtReportsPage',
        DEBT_REPORT_GROUP.PAYABLES,
        PAYABLE_REPORT_KEY.DEBT_REPORT
      ),
      replace: true,
    }),
  },
  {
    path: '/accounting/payables/supplier-opening-balance',
    element: createElement(Navigate, {
      to: getPath(
        'accountingManagementPage',
        ACCOUNTING_MANAGEMENT_TAB.SUPPLIERS
      ),
      replace: true,
    }),
  },

  // ---------------------------------------------------------------------------
  // Module: Hành chính nhân sự (HR)
  // ---------------------------------------------------------------------------
  {
    path: '/hr',
    element: createElement(Navigate, {
      to: getPath('hrPersonnelPage'),
      replace: true,
    }),
  },
  {
    path: getPath('hrPersonnelPage'),
    element: createElement(
      lazyImport(() => import('modules/HR/PersonnelPage')),
      { pageTitle: 'Quản lý nhân sự' }
    ),
    breadcrumbs: [
      { title: 'Hành chính - nhân sự' },
      { title: 'Quản lý nhân sự' },
    ],
  },
  {
    path: getPath('hrTimekeepingPage'),
    element: createElement(
      lazyImport(() => import('modules/HR/TimekeepingPage')),
      { pageTitle: 'Quản lý chấm công' }
    ),
    breadcrumbs: [
      { title: 'Hành chính - nhân sự' },
      { title: 'Quản lý chấm công' },
    ],
  },
  {
    path: getPath('hrAssetPage'),
    element: createElement(
      lazyImport(() => import('modules/HR/AssetPage')),
      { pageTitle: 'Quản lý tài sản' }
    ),
    breadcrumbs: [
      { title: 'Hành chính - nhân sự' },
      { title: 'Quản lý tài sản' },
    ],
  },
  {
    path: getPath('hrDepartmentPage'),
    element: createElement(
      lazyImport(() => import('modules/HR/DepartmentPage')),
      { pageTitle: 'Phòng ban' }
    ),
    breadcrumbs: [{ title: 'Hành chính - nhân sự' }, { title: 'Phòng ban' }],
  },

  // ---------------------------------------------------------------------------
  // Dev / Storybook — chỉ dùng cho môi trường phát triển
  // ---------------------------------------------------------------------------

  /** Trang thư viện icon — xem toàn bộ icon đang dùng trong dự án */
  {
    path: getPath('iconGalleryPage'),
    element: import.meta.env.DEV
      ? createElement(
          lazyImport(() => import('shared/components/AppIcon/IconGallery'))
        )
      : createElement(
          lazyImport(() =>
            import('shared/components/ForbiddenAccessNotice').then(m => ({
              default: m.ForbiddenAccessNotice,
            }))
          )
        ),
  },
];
